/**
 * ScanJoy - © 2025 Juan Pablo Marín Del Rincón. Todos los derechos reservados.
 *
 * Este sistema fue desarrollado exclusivamente para Dinámica Juvenil Sfa.
 * Se prohíbe su venta, redistribución, modificación o uso no autorizado,
 * total o parcial, sin consentimiento por escrito del autor.
 *
 * Protegido por la Ley Federal del Derecho de Autor (LFDA), Título VII:
 * - Capítulo I: Infracciones Administrativas
 * - Capítulo II: Delitos
 * - Capítulo III: Sanciones
 *
 * Cualquier uso indebido podrá ser sancionado conforme a la ley.
 * Contacto: jpmarin1999.@gmail.com
 */
import React, { useContext } from "react";
import { Layout, ConfigProvider, theme as antdTheme, App as AntdApp } from "antd";
import { ThemeProvider, ThemeContext } from "./context/ThemeContext";
import { Route, Routes, useLocation } from "react-router-dom";
import { sileo, Toaster } from "sileo";
import Navbar from "./components/Navbar";
import Login from "./pages/Auth/Login";
import ForgotPassword from "./pages/Auth/ForgotPassword";
import Dashboard from "./pages/Function/Dashboard";
import QRScanner from "./pages/Function/QRScanner";
import FileExplorer from "./pages/Function/Events";
import NotFoundPage from "./pages/Status/NotFoundPage";
import ForbiddenPage from "./pages/Status/ForbiddenPage";
import ServerErrorPage from "./pages/Status/ServerErrorPage";
import Registro from "./pages/Auth/Registro";
import ProtectedRoute from "./components/ProtectedRoute";
import Mantenimiento from "./pages/Status/Mantenimiento";
import Suspencion from "./pages/Status/Suspencion";
import Perfil from "./pages/Function/Perfil";
import Store from "./pages/Function/Store";
import BirthdayList from "./pages/Function/BirthdayList";
import IGNIS from "./pages/Function/SorteoTribu";
import Aniversario from "./pages/Function/Aniversario";
import Ranking from "./pages/Function/Ranking";
import Loader from "./pages/Status/Loader";
import FeedbackForm from "./pages/Function/FeedBackForm";
import GamesDashboard from "./pages/Function/Games/GamesDashboard";
import  Snake  from "./pages/Function/Games/Snake";
import AdminDashboard from "./pages/Admin/AdminDashboard";
import AdminQRDashboard from "./pages/Admin/AdminQRDashboard";
import AdminActivividadesDashboard from "./pages/Admin/AdminActivividadesDashboard";
import AdminUsersDashboard from "./pages/Admin/AdminUsuariosDashboard";
import AdminStoreDashboard from "./pages/Admin/AdminStoreDashboard";
import AdminFeedbackDashboard from "./pages/Admin/AdminFeedbackDashboard";
import AdminNotificationsDashboard from "./pages/Admin/AdminNotificationsDashboard";
import AdminEventsDashboard from "./pages/Admin/AdminEventsDashboard";
import AdminBravosDashboard from "./pages/Admin/AdminBravosDashboard";
import AdminMenuDashboard from "./pages/Admin/AdminMenuDashboard";
import AdminKermesDashboard from "./pages/Admin/AdminKermesDashboard";
import SuperAdminDashboard from "./pages/SuperAdmin/SuperAdminDashboard";
import { AuthProvider } from "./context/AuthProvider";
import { getBackendUrl } from "./utils/env";
import axios from "axios";
import "./theme.css";
import InstallPrompt from "./components/InstallPrompt";

const { Header, Content } = Layout;

function AppContent() {
  const { theme } = useContext(ThemeContext); // 👈 Ahora sí funciona
  const isDark = theme === "dark";
  const { token } = antdTheme.useToken();

  const [systemStatus, setSystemStatus] = React.useState(null);
  const location = useLocation();
  const hideNavbarRoutes = ["/", "/register","/forgot-password", "/forbidden", "/server-error", "/mantenimiento","/admin/kermes"];
  const hideNavbar = hideNavbarRoutes.includes(location.pathname);

  React.useEffect(() => {
    axios
      .get(`${getBackendUrl()}config/system.php`)
      .then((res) => {
        const payload = res.data.data ?? res.data;
        if (!payload) {
          throw new Error("Respuesta inválida del servidor");
        }
        setSystemStatus({
          active: payload.active === true || payload.active === "active",
          maintenance: payload.maintenance === true || payload.maintenance === "active",
          suspension: payload.suspension === true || payload.suspension === "active",
        });
      })
      .catch((error) => {
        // console.error("Error al obtener el estado del sistema:", error);
        setSystemStatus({
          active: true,
          maintenance: false,
          suspension: false,
        });
      });
  }, []);

  if (!systemStatus) {
    return <Loader />;
  }

  if (systemStatus.maintenance) {
    return <Mantenimiento />;
  }

  if (systemStatus.suspension || !systemStatus.active) {
    return <Suspencion />;
  }


  
  return (
    <ConfigProvider
      theme={{
        algorithm: isDark ? antdTheme.darkAlgorithm : antdTheme.compactAlgorithm,
      }}
    >
      <AntdApp>
        <Layout style={{ minHeight: "100vh" }}>
          <InstallPrompt />
          {!hideNavbar && (
            <Header style={{ padding: 0 }}>
              <Navbar />
            </Header>
          )}
          <Content style={{ padding: "20px" }}>
            <Routes>
              <Route path="/" element={<Login />} />
              <Route path="/register" element={<Registro />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
              <Route path="/scan" element={<ProtectedRoute><QRScanner /></ProtectedRoute>} />
              <Route path="/perfil" element={<ProtectedRoute><Perfil /></ProtectedRoute>} />
              <Route path="/store" element={<ProtectedRoute><Store /></ProtectedRoute>} />
              <Route path="/ranking" element={<ProtectedRoute><Ranking /></ProtectedRoute>} />
              <Route path="/events" element={<ProtectedRoute><FileExplorer /></ProtectedRoute>} />
              <Route path="/feedback" element={<ProtectedRoute><FeedbackForm /></ProtectedRoute>} />
              <Route path="/birthdays" element={<ProtectedRoute><BirthdayList /></ProtectedRoute>} />
              <Route path="/ignis" element={<ProtectedRoute><IGNIS /></ProtectedRoute>} />
              <Route path="/aniversario" element={<ProtectedRoute><Aniversario /></ProtectedRoute>} />
              <Route path="/games" element={<ProtectedRoute><GamesDashboard /></ProtectedRoute>} />
              <Route path="/games/snake" element={<ProtectedRoute><Snake/></ProtectedRoute>} />
              <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
              <Route path="/admin/qr" element={<ProtectedRoute><AdminQRDashboard /></ProtectedRoute>} />
              <Route path="/admin/activities" element={<ProtectedRoute><AdminActivividadesDashboard /></ProtectedRoute>} />
              <Route path="/admin/users" element={<ProtectedRoute><AdminUsersDashboard /></ProtectedRoute>} />
              <Route path="/admin/store" element={<ProtectedRoute><AdminStoreDashboard /></ProtectedRoute>} />
              <Route path="/admin/feedback" element={<ProtectedRoute><AdminFeedbackDashboard /></ProtectedRoute>} />
              <Route path="/admin/notifications" element={<ProtectedRoute><AdminNotificationsDashboard /></ProtectedRoute>} />
              <Route path="/admin/menu" element={<ProtectedRoute><AdminMenuDashboard /></ProtectedRoute>} />
              <Route path="/superadmin" element={<ProtectedRoute><SuperAdminDashboard /></ProtectedRoute>} />
              <Route path="/admin/events" element={<ProtectedRoute><AdminEventsDashboard /></ProtectedRoute>} />
              <Route path="/admin/bravos" element={<ProtectedRoute><AdminBravosDashboard /></ProtectedRoute>} />
              <Route path="/admin/kermes" element={<ProtectedRoute><AdminKermesDashboard /></ProtectedRoute>} />
              <Route path="/mantenimiento" element={<Mantenimiento />} />
              <Route path="/forbidden" element={<ForbiddenPage />} />
              <Route path="/server-error" element={<ServerErrorPage />} />
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Content>
          <p
            style={{
              fontSize: 10,
              textAlign: "center",
              marginTop: 50,
              marginBottom: 90, // Espacio para el menú pildora
              color: token.colorWarningText,
            }}
          >
            © {new Date().getFullYear()} - Sistema desarrollado por Juan Pablo Marín Del
            Rincón. Uso exclusivo para Dinámica Juvenil Sfa.
          </p>
        </Layout>
      </AntdApp>
    </ConfigProvider>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <ThemeProvider> {/* 👈 Aquí envuelve TODO */}
        <Toaster
          options={{
            fill: "var(--ant-primary-color)",
            styles: { description: "text-white/75!" },
          }}
          position="top-center"
        />
        <AppContent />
      </ThemeProvider>
    </AuthProvider>
  );
}
